<script lang="ts">
  // Drag-to-edit handles for the duel field (Texture Pack editor). EACH SIDE is edited independently:
  // your-side handles are CYAN, the opponent's are MAGENTA, and each writes its own per-side vars
  // (--df-z-<side>-<id>-<prop>) so moving your monster row doesn't move theirs. Per group (monster &
  // spell rows + field/extra/graveyard/deck/banish piles): a MOVE grip pinned to the top-right corner
  // (drag → x/y, 1:1 on screen), plus SIZE −/+ (and GAP −/+ on the rows) that reveal on hover. Groups
  // are located via data-fe="<id>" + data-fe-side="me|opp"; everything writes through setBlockVar.
  import { blockVar, setBlockVar } from "../stores/theme.svelte.ts";

  const GROUPS: { id: string; label: string; gap?: boolean; rot?: boolean }[] = [
    { id: "mzone", label: "Monster zones", gap: true },
    { id: "szone", label: "Spell / Trap zones", gap: true },
    { id: "field", label: "Field spell" },
    { id: "extra", label: "Extra deck" },
    { id: "gy", label: "Graveyard" },
    { id: "deck", label: "Deck" },
    { id: "banish", label: "Banish", rot: true },
    { id: "hand", label: "Hand", gap: true },
  ];
  const SIDES = [
    { side: "me", who: "Your", accent: "#19e0ff" },
    { side: "opp", who: "Opponent", accent: "#ff2d7e" },
  ] as const;

  const num = (v: string): number => parseFloat(v) || 0;
  const vkey = (side: string, id: string, prop: string): string => `--df-z-${side}-${id}-${prop}`;

  type Box = { x: number; y: number; w: number; h: number };
  /** The element's rect, or — for a full-width container like the hand — the union of its children. */
  function boxOf(el: Element, ofChildren: boolean): Box {
    if (ofChildren && el.children.length) {
      let l = Infinity, t = Infinity, r = -Infinity, b = -Infinity;
      for (const c of el.children) { const cr = c.getBoundingClientRect(); if (cr.width) { l = Math.min(l, cr.left); t = Math.min(t, cr.top); r = Math.max(r, cr.right); b = Math.max(b, cr.bottom); } }
      if (r > l) return { x: l, y: t, w: r - l, h: b - t };
    }
    const rc = el.getBoundingClientRect();
    return { x: rc.left, y: rc.top, w: rc.width, h: rc.height };
  }

  // ── live geometry: each side+group rect + the stage scale, refreshed every frame ──
  let rects = $state<Record<string, Box | null>>({});
  let scale = 1;
  $effect(() => {
    let raf = 0;
    const tick = () => {
      const stage = document.querySelector(".duel-stage");
      if (stage) { try { scale = new DOMMatrix(getComputedStyle(stage).transform).a || 1; } catch { /* not laid out yet */ } }
      const next: Record<string, Box | null> = {};
      for (const s of SIDES) for (const g of GROUPS) {
        const el = document.querySelector(`[data-fe="${g.id}"][data-fe-side="${s.side}"]`);
        // The hand container is full-width but its cards are centred, so measure the cards' extent.
        const b = el ? boxOf(el, g.id === "hand") : null;
        next[`${s.side}:${g.id}`] = b;
      }
      rects = next;
      raf = requestAnimationFrame(tick);
    };
    tick();
    return () => cancelAnimationFrame(raf);
  });

  // ── drag a group (writes that side's --df-z-<side>-<id>-x/y) ──
  let dragKey = $state<string | null>(null);
  let drag: { side: string; id: string; sx: number; sy: number; vx: number; vy: number } | null = null;
  function startDrag(side: string, id: string, e: PointerEvent): void {
    e.preventDefault();
    e.stopPropagation();
    dragKey = `${side}:${id}`;
    drag = { side, id, sx: e.clientX, sy: e.clientY, vx: num(blockVar("duelfield", vkey(side, id, "x"))), vy: num(blockVar("duelfield", vkey(side, id, "y"))) };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", endDrag, { once: true });
  }
  function onMove(e: PointerEvent): void {
    if (!drag) return;
    setBlockVar("duelfield", vkey(drag.side, drag.id, "x"), Math.round(drag.vx + (e.clientX - drag.sx) / scale) + "px");
    setBlockVar("duelfield", vkey(drag.side, drag.id, "y"), Math.round(drag.vy + (e.clientY - drag.sy) / scale) + "px");
  }
  function endDrag(): void { dragKey = null; drag = null; window.removeEventListener("pointermove", onMove); }

  // ── steppers ──
  function bumpGap(side: string, id: string, d: number): void {
    const v = Math.min(40, Math.max(0, (num(blockVar("duelfield", vkey(side, id, "gap"))) || 6) + d));
    setBlockVar("duelfield", vkey(side, id, "gap"), Math.round(v) + "px");
  }
  function bumpSize(side: string, id: string, d: number): void {
    const v = Math.min(2, Math.max(0.4, (num(blockVar("duelfield", vkey(side, id, "size"))) || 1) + d));
    setBlockVar("duelfield", vkey(side, id, "size"), v.toFixed(2));
  }
  function bumpRot(side: string, id: string, d: number): void {
    const v = ((num(blockVar("duelfield", vkey(side, id, "rot"))) + d + 180) % 360 + 360) % 360 - 180;
    setBlockVar("duelfield", vkey(side, id, "rot"), Math.round(v) + "deg");
  }
</script>

{#snippet stepper(label: string, minus: () => void, plus: () => void)}
  <div class="fe-pill flex items-center" title={label}>
    <button onclick={minus} class="fe-step" aria-label="{label} smaller">
      <svg viewBox="0 0 24 24" class="size-3" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"><path d="M5 12h14" /></svg>
    </button>
    <span class="fe-label px-1 font-display text-[0.5rem] font-bold uppercase tracking-[0.12em]">{label}</span>
    <button onclick={plus} class="fe-step" aria-label="{label} larger">
      <svg viewBox="0 0 24 24" class="size-3" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"><path d="M12 5v14" /><path d="M5 12h14" /></svg>
    </button>
  </div>
{/snippet}

{#each SIDES as s (s.side)}
  {#each GROUPS as g (g.id)}
    {@const r = rects[`${s.side}:${g.id}`]}
    {#if r}
      <div
        class="fe-zone group/fe pointer-events-auto fixed"
        class:fe-active={dragKey === `${s.side}:${g.id}`}
        style="left:{r.x}px;top:{r.y}px;width:{r.w}px;height:{r.h}px;--fe-accent:{s.accent}"
        role="group"
        aria-label="Edit {s.who} {g.label}"
      >
        <!-- move grip — always visible, pinned to the top-right corner -->
        <button
          onpointerdown={(e) => startDrag(s.side, g.id, e)}
          class="fe-grip absolute -right-2.5 -top-2.5 z-10"
          title="Drag to move · {s.who} {g.label}"
          aria-label="Move {s.who} {g.label}"
        >
          <svg viewBox="0 0 24 24" class="size-3.5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 9 2 12l3 3" /><path d="m9 5 3-3 3 3" /><path d="M15 19l-3 3-3-3" /><path d="m19 9 3 3-3 3" /><path d="M2 12h20" /><path d="M12 2v20" /></svg>
        </button>

        <!-- size (centre) + gap (below, rows only) — reveal on hover/drag -->
        <div class="fe-tools pointer-events-none absolute inset-0 flex flex-col items-center justify-center gap-1">
          {@render stepper("size", () => bumpSize(s.side, g.id, -0.05), () => bumpSize(s.side, g.id, 0.05))}
          {#if g.gap}{@render stepper("gap", () => bumpGap(s.side, g.id, -1), () => bumpGap(s.side, g.id, 1))}{/if}
          {#if g.rot}{@render stepper("rot", () => bumpRot(s.side, g.id, -15), () => bumpRot(s.side, g.id, 15))}{/if}
        </div>
      </div>
    {/if}
  {/each}
{/each}

<style>
  /* Handles are EDITOR CHROME — they use a FIXED per-side accent (--fe-accent: cyan for you, magenta
     for the opponent), not the themed var(--color-*) which the active pack overrides. Chamfered +
     glowing to match the VRAINS data-window language. */
  .fe-zone {
    box-shadow: inset 0 0 0 1px color-mix(in oklab, var(--fe-accent) 18%, transparent);
    border-radius: 3px;
    transition: box-shadow 160ms cubic-bezier(0.22, 1, 0.36, 1);
  }
  .fe-zone:hover,
  .fe-zone.fe-active {
    box-shadow: inset 0 0 0 1px color-mix(in oklab, var(--fe-accent) 75%, transparent), 0 0 14px color-mix(in oklab, var(--fe-accent) 28%, transparent);
  }

  .fe-grip {
    display: grid;
    place-items: center;
    width: 1.35rem;
    height: 1.35rem;
    cursor: move;
    color: var(--fe-accent);
    background: rgb(6 9 18 / 0.9);
    box-shadow: inset 0 0 0 1px color-mix(in oklab, var(--fe-accent) 55%, transparent);
    backdrop-filter: blur(4px);
    clip-path: polygon(28% 0, 100% 0, 100% 72%, 72% 100%, 0 100%, 0 28%);
    opacity: 0.85;
    transition: opacity 160ms cubic-bezier(0.22, 1, 0.36, 1), box-shadow 160ms ease-out;
  }
  .fe-grip:hover,
  .fe-active .fe-grip {
    opacity: 1;
    box-shadow: inset 0 0 0 1px var(--fe-accent), 0 0 12px color-mix(in oklab, var(--fe-accent) 65%, transparent);
  }

  /* steppers hidden until you point at (or drag) the group, so the board stays clean */
  .fe-tools {
    opacity: 0;
    transition: opacity 160ms cubic-bezier(0.22, 1, 0.36, 1);
  }
  .group\/fe:hover .fe-tools,
  .fe-active .fe-tools { opacity: 1; }

  .fe-pill {
    pointer-events: auto;
    background: rgb(6 9 18 / 0.92);
    box-shadow: inset 0 0 0 1px color-mix(in oklab, var(--fe-accent) 45%, transparent);
    backdrop-filter: blur(4px);
    clip-path: polygon(7% 0, 93% 0, 100% 50%, 93% 100%, 7% 100%, 0 50%);
    padding: 0.05rem 0.15rem;
  }
  .fe-label { color: var(--fe-accent); }
  .fe-step {
    display: grid;
    place-items: center;
    width: 1.05rem;
    height: 1.05rem;
    color: #8fb6d6;
    transition: color 120ms ease-out, background-color 120ms ease-out;
  }
  .fe-step:hover { color: var(--fe-accent); background: color-mix(in oklab, var(--fe-accent) 18%, transparent); }

  @media (prefers-reduced-motion: reduce) {
    .fe-zone, .fe-grip, .fe-tools { transition: none; }
  }
</style>
